import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, CircularProgress, Menu, MenuItem, Modal, Select, Typography } from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { useToast } from 'use-toast-mui';

import {
  EditModalContainer,
  EditHeader,
  EditHeaderTitle,
  EditHeaderClose,
  EditBody,
  PoolPanel,
  PanelGap,
  PickedPanel,
  SearchRow,
  SearchField,
  FilterToggleButton,
  ResultRow,
  ResultLabel,
  AddAllButton,
  ListHead,
  ListScroll,
  PoolRow,
  PickedRow,
  RowCheck,
  Cell,
  HeadCell,
  NameCell,
  RemoveRowButton,
  EmptyMessage,
  PickedHeader,
  PickedTitle,
  PickedSearchField,
  FilterChip,
  EditFooter,
  SummaryText,
  SummaryStrong,
  SummaryMuted,
  FooterActions,
  CancelButton,
  ApplyButton,
} from '../../../member/modal/MemberRecipientPickerModalStyled';
import { getGroupBatchReserveCandidates, getGroupReserveCandidates } from '../../../../api/lesson';
import { getMemberGroups } from '../../../../api/member-group';
import { errorMessages } from '../../../../utils/methods';

const matchSearch = (row, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return (row.name || '').toLowerCase().includes(q) || (row.phoneNumber || '').includes(q);
};

// 남은 횟수가 다른 기존 수업의 RESERVED 차감으로 이미 0 인 회원 — 새 회차를 하나도 잡을 수 없으므로 선택을 막는다.
const isFullyReserved = row => row.remainingCount != null && row.remainingCount <= 0;
// 선택한 회차 전부에 이미 예약/대기 중인 회원 — 새로 잡을 회차가 없으므로 선택을 막는다(회차가 하나면 곧 "예약된 회원").
// 일부만 예약된 회원은 그 회차만 건너뛰고 나머지를 잡는다(사용자 확정 2026-09-23).
const isAlreadyReservedAll = (row, classCount) => classCount > 0 && (row.alreadyReservedCount || 0) >= classCount;
// 잔여가 선택 회차의 차감 합보다 모자란 회원 — 전부-아니면-전무라 어차피 통째로 제외되므로 담기 전에 막는다(백엔드가 준 shortageCount).
const shortageOf = row => row.shortageCount || 0;
// 이번 예약에 쓸 수 있는 상품만 — 잔여가 필요 차감(requiredCount) 이상인 것(무제한 포함). 담긴 회원 패널의 선택지.
const usableTickets = row => (row.tickets || []).filter(t => {
  const need = row.requiredCount ?? 0;
  return t.remainingCount == null || t.remainingCount >= need;
});

// 칩 키 — 수강권과 일일권은 상품 ID 체계가 달라 종류까지 묶어야 한다.
const tkey = (id, type) => `${type || 'COURSE'}:${id}`;
const rowTicketKeys = row => (row.tickets && row.tickets.length > 0
  ? row.tickets.map(t => tkey(t.courseTicketId, t.ticketType))
  : (row.ticketIds || []).map(id => tkey(id, 'COURSE')));
// 활성 칩이 없으면 전체, 있으면 그 중 하나라도 보유(OR).
const matchTickets = (row, activeKeys) => activeKeys.length === 0
  || rowTicketKeys(row).some(k => activeKeys.includes(k));

// 상한(limit, null 이면 없음)까지만 추가한다. 이미 담긴 회원은 건너뛴다.
const toLimit = maxCount => (maxCount == null || maxCount === '' || !Number.isFinite(Number(maxCount)) ? null : Math.max(0, Number(maxCount)));
const appendUpToLimit = (picked, candidates, limit) => {
  const ids = new Set(picked.map(m => m.memberId));
  const next = [...picked];
  let added = 0;
  let skipped = 0;
  for (const row of candidates) {
    if (ids.has(row.memberId)) continue;
    if (limit != null && next.length >= limit) { skipped += 1; continue; }
    next.push(row);
    ids.add(row.memberId);
    added += 1;
  }
  return { next, added, skipped };
};

// 칩 한 줄 높이(FilterChip 30px). 접힌 상태의 칩 영역 높이이자 "둘째 줄부터 숨김" 기준.
const CHIP_ROW_HEIGHT = 30;
const CHIP_ROW_GAP = 6;
// 펼쳐도 이 줄 수까지만 보이고 그 아래는 스크롤 — 칩이 수십 개면 회원 목록이 밀려 사라진다.
const CHIP_EXPANDED_ROWS = 4;

/**
 * 그룹수업 생성 시 함께 예약할 회원 선택 — 문자 수신자 편집과 같은 구조.
 *
 * 왼쪽은 후보(선택한 수강권 상품을 보유한 유효 회원)를 검색·수강권 칩·회원 그룹으로 좁혀 체크해 담고,
 * 오른쪽은 담긴 회원. "그룹 불러오기"는 왼쪽 목록을 그 그룹 소속으로 좁힐 뿐 담기는 체크 흐름 그대로다.
 * 담기는 정원(maxCount)까지만(대기 등록 없음 — 사용자 확정). 회차별 정확한 판정은 등록 전 미리보기가 한다.
 * 담은 결과는 "선택 완료"에서만 부모에 반영하고, 취소·닫기는 버린다.
 *
 * 본문(좌·우 패널)은 {@link ReserveMemberPicker} 로 분리돼 있어 그룹수업 일괄예약 마법사(단계 2)에서도 그대로 쓴다.
 */

/** 회원 찾기·담기 본문. 담긴 목록(picked)은 부모가 들고 있다(controlled). */
export const ReserveMemberPicker = ({
  open, // true 가 되는 순간 검색·필터를 초기화하고 후보를 다시 읽는다
  token,
  branchId,
  ticketIds = [],
  ticketOptions = [], // [{id, name}] 스케줄 모달에서 고른 수강권(일일권 제외)
  startDate,
  endDate,
  // 기존 그룹수업 일괄예약: 선택한 수업 ID. 있으면 후보를 수업 기준으로 조회한다(ticketIds·기간 대신).
  courseClassIds = null,
  courseTicketIds = null, // 일괄예약 1단계에서 고른 수강권들 — 후보를 그 상품 보유자로 한정
  trialTicketIds = null, // 1단계에서 고른 일일권들(같은 역할)
  maxCount, // 담을 수 있는 최대 인원. null/빈값 = 제한 없음, 0 = 한 명도 못 담음(정원 마감)
  limitLabel = '정원',
  // 회원별로 쓸 상품(회원ID → 상품ID). 선택 수강권이 여럿이고 회원이 여러 개 보유할 때 담긴 회원 패널에서 고른다.
  memberTicketIds = {}, // 회원ID → 상품ID(수강권·일일권 공용, 종류는 후보의 ticketType 으로 구분)
  onMemberTicketChange, // 상한 안내 문구 — 일괄예약은 "선택한 수업들의 최소 남은 예약 자리 수"
  picked = [], // 담긴 회원 [{memberId, name, ...}]
  onPickedChange,
}) => {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [groupAnchor, setGroupAnchor] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTicketIds, setActiveTicketIds] = useState([]); // 칩 키("종류:ID") 목록
  // 기본값 = 보유자 있는 수강권만. 수업에 매핑된 상품이 수십 개라도 실제 후보가 가진 것만 칩으로 보인다(체크 해제하면 전부).
  const [onlyHeldTickets, setOnlyHeldTickets] = useState(true);
  const [chipsExpanded, setChipsExpanded] = useState(false);
  const [hiddenChipCount, setHiddenChipCount] = useState(0);
  const [checkedIds, setCheckedIds] = useState(new Set()); // 왼쪽 목록에서 체크한(아직 안 담은) 회원
  const [pickedSearch, setPickedSearch] = useState('');
  // MUI Modal 은 Portal 이 한 렌더 뒤에 붙어 ref 가 처음엔 비어 있다 — 노드를 state 로 받아 붙은 뒤에 잰다.
  const [chipArea, setChipArea] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setPickedSearch('');
    setGroupId(null);
    setGroupAnchor(null);
    setActiveTicketIds([]);
    setOnlyHeldTickets(true);
    setChipsExpanded(false);
    setCheckedIds(new Set());
    getMemberGroups(token, branchId)
      .then(data => setGroups(data || []))
      .catch(() => setGroups([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    setCheckedIds(new Set());
    const fetchCandidates = courseClassIds
      ? getGroupBatchReserveCandidates({ courseClassIds, memberGroupId: groupId, courseTicketIds, trialTicketIds }, token)
      : getGroupReserveCandidates({ ticketIds, startDate, endDate, memberGroupId: groupId }, token);
    fetchCandidates
      .then(data => { if (alive) setRows(data || []); })
      .catch(error => { if (alive) { setRows([]); toast.error(errorMessages(error)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, groupId]);

  // 수강권별 보유자 수(현재 그룹 기준). 칩 라벨과 "보유 회원 있는 수강권만" 토글에 쓴다.
  const holderCountByTicket = useMemo(() => {
    const counts = {};
    ticketOptions.forEach(t => { counts[tkey(t.id, t.ticketType)] = 0; });
    rows.forEach(row => rowTicketKeys(row).forEach(k => { if (k in counts) counts[k] += 1; }));
    return counts;
  }, [rows, ticketOptions]);
  const visibleTicketOptions = useMemo(
    () => (onlyHeldTickets ? ticketOptions.filter(t => holderCountByTicket[tkey(t.id, t.ticketType)] > 0) : ticketOptions),
    [ticketOptions, onlyHeldTickets, holderCountByTicket],
  );
  const ticketNameById = useMemo(
    () => Object.fromEntries(ticketOptions.map(t => [tkey(t.id, t.ticketType), t.name])),
    [ticketOptions],
  );

  // 토글로 칩이 숨겨지면 그 칩의 활성 상태도 푼다(보이지 않는 필터가 남지 않도록).
  useEffect(() => {
    const visibleIds = new Set(visibleTicketOptions.map(t => tkey(t.id, t.ticketType)));
    setActiveTicketIds(prev => (prev.every(id => visibleIds.has(id)) ? prev : prev.filter(id => visibleIds.has(id))));
  }, [visibleTicketOptions]);

  // 칩이 둘째 줄로 넘어가면 접고 "+N" 으로 알린다. 칩 목록·영역 폭이 바뀔 때 다시 잰다.
  useLayoutEffect(() => {
    if (!chipArea) return undefined;
    const measure = () => {
      const chips = Array.from(chipArea.children).filter(c => c.tagName !== 'P');
      if (chips.length === 0) { setHiddenChipCount(0); return; }
      const firstTop = chips[0].offsetTop;
      setHiddenChipCount(chips.filter(c => c.offsetTop > firstTop).length);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(chipArea);
    return () => observer.disconnect();
  }, [chipArea, visibleTicketOptions, chipsExpanded]);

  const pickedIds = useMemo(() => new Set(picked.map(m => m.memberId)), [picked]);
  const limit = toLimit(maxCount);

  // 왼쪽 목록 = 후보 중 아직 안 담긴 회원, 검색·칩 필터 적용.
  const poolRows = useMemo(
    () => rows.filter(row => !pickedIds.has(row.memberId) && matchTickets(row, activeTicketIds) && matchSearch(row, search)),
    [rows, pickedIds, activeTicketIds, search],
  );
  const classCount = courseClassIds ? courseClassIds.length : 0;
  // 칩으로 수강권을 걸러 보는 동안은 그 수강권 기준으로 판정한다 —
  // "이 수강권으로 예약할 회원"을 고르는 화면인데 잔여가 모자란 회원이 남아 있으면 담은 뒤 다른 상품으로 바뀐다.
  const usableUnderFilter = row => {
    const usable = usableTickets(row);
    if (activeTicketIds.length === 0) {
      return usable;
    }
    return usable.filter(t => activeTicketIds.includes(tkey(t.courseTicketId, t.ticketType)));
  };
  const selectableRows = useMemo(
    () => poolRows.filter(r => !isFullyReserved(r) && !isAlreadyReservedAll(r, classCount)
      && shortageOf(r) === 0 && usableUnderFilter(r).length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [poolRows, classCount, activeTicketIds],
  );
  const checkedVisibleCount = selectableRows.filter(r => checkedIds.has(r.memberId)).length;
  // 회원을 체크하면 다음 동작이 "담기"라는 걸 알 수 있게 담기 버튼에 포커스(링 강조)를 준다. 0→1 이 될 때 한 번만.
  const addButtonRef = useRef(null);
  const prevCheckedRef = useRef(0);
  useEffect(() => {
    if (prevCheckedRef.current === 0 && checkedVisibleCount > 0) addButtonRef.current?.focus();
    prevCheckedRef.current = checkedVisibleCount;
  }, [checkedVisibleCount]);
  const allVisibleChecked = selectableRows.length > 0 && checkedVisibleCount === selectableRows.length;
  const visiblePicked = useMemo(() => picked.filter(row => matchSearch(row, pickedSearch)), [picked, pickedSearch]);

  const toggleTicketChip = key => {
    setActiveTicketIds(prev => (prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]));
  };

  const toggleChecked = row => {
    if (isAlreadyReservedAll(row, classCount)) {
      toast.error(`${row.name} 님은 이미 예약된 회원입니다`);
      return;
    }
    if (shortageOf(row) > 0) {
      toast.error(`${row.name} 님은 잔여 세션이 ${shortageOf(row)}회 부족합니다`);
      return;
    }
    if (usableUnderFilter(row).length === 0) {
      toast.error(`${row.name} 님은 고른 수강권의 잔여가 이번 예약(${row.requiredCount ?? 0}회)에 모자랍니다`);
      return;
    }
    if (isFullyReserved(row)) {
      toast.error(`${row.name} 님은 남은 횟수가 다른 수업 예약에 모두 잡혀 있어 선택할 수 없습니다`);
      return;
    }
    const { memberId } = row;
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId); else next.add(memberId);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (allVisibleChecked) selectableRows.forEach(r => next.delete(r.memberId));
      else selectableRows.forEach(r => next.add(r.memberId));
      return next;
    });
  };

  // 담을 때 쓸 상품 기본값 — 쓸 수 있는 것 중 만료가 빠른 것부터(먼저 소진되게).
  const assignDefaultTickets = rows2 => {
    if (!onMemberTicketChange) return;
    rows2.forEach(row => {
      if (memberTicketIds[row.memberId]) return;
      const usable = usableUnderFilter(row);
      if (usable.length === 0) return;
      const byExpire = [...usable].sort((a, b) => (a.expireDate || '9999').localeCompare(b.expireDate || '9999'));
      // 1) 칩으로 걸러서 담았으면 그 상품이 기본 — 목록을 그 수강권으로 보고 담았기 때문.
      const filtered = byExpire.find(t => activeTicketIds.includes(tkey(t.courseTicketId, t.ticketType)));
      // 고른 칩의 상품이 잔여 부족으로 못 쓰이면 조용히 바뀌지 않게 알린다.
      const blockedByChip = !filtered && (row.tickets || [])
        .find(t => activeTicketIds.includes(tkey(t.courseTicketId, t.ticketType)));
      if (blockedByChip) {
        toast.info(`${row.name} 님의 ${blockedByChip.ticketName}은 잔여가 ${blockedByChip.remainingCount ?? 0}회라 이번 예약(${row.requiredCount ?? 0}회)에 쓸 수 없어 다른 수강권으로 담았습니다`);
      }
      // 2) 필터가 없으면 수강권 우선(일일권은 수강권이 없을 때만).
      const picked = filtered || byExpire.find(t => t.ticketType !== 'TRIAL') || byExpire[0];
      onMemberTicketChange(row.memberId, picked.courseTicketId, picked.ticketType);
    });
  };

  // 체크한 회원을 정원까지 담는다. 넘치는 만큼은 남겨 두고 안내한다.
  const addChecked = () => {
    const targets = selectableRows.filter(r => checkedIds.has(r.memberId));
    if (targets.length === 0) return;
    const { next, added, skipped } = appendUpToLimit(picked, targets, limit);
    if (added === 0) {
      toast.error(`${limitLabel}(${limit}명)를 넘어 더 담을 수 없습니다`);
      return;
    }
    onPickedChange(next);
    assignDefaultTickets(next);
    setCheckedIds(prev => {
      const rest = new Set(prev);
      next.forEach(m => rest.delete(m.memberId));
      return rest;
    });
    if (skipped > 0) toast.warning(`${added}명 담았습니다. ${limitLabel}(${limit}명)를 넘어 ${skipped}명은 담지 못했습니다`);
    // 이미 예약된 회차가 있는 회원은 그 회차를 건너뛰고 나머지만 잡힌다 — 담는 시점에 알려 준다.
    const partial = targets.filter(r => (r.alreadyReservedCount || 0) > 0);
    if (partial.length > 0) {
      toast.info(partial.length === 1
        ? `${partial[0].name} 님은 이미 예약된 ${partial[0].alreadyReservedCount}회를 빼고 나머지 수업만 예약됩니다`
        : `${partial.length}명은 이미 예약된 회차를 빼고 나머지 수업만 예약됩니다`);
    }
  };

  const removePicked = memberId => onPickedChange(picked.filter(m => m.memberId !== memberId));

  const selectedGroup = groups.find(g => g.id === groupId) || null;

  const renderTicketLabel = row => {
    const heldNames = rowTicketKeys(row).map(k => ticketNameById[k]).filter(Boolean);
    if (heldNames.length === 0) return `${row.ticketName}${row.ticketCount > 1 ? ` 외 ${row.ticketCount - 1}` : ''}`;
    return `${heldNames[0]}${heldNames.length > 1 ? ` 외 ${heldNames.length - 1}` : ''}`;
  };
  // 만료일은 YY.MM.DD 로 줄여 한 줄에 맞춘다.
  const renderRemaining = row =>
    `${row.remainingCount != null ? `잔여 ${row.remainingCount}회` : '무제한'}${row.expireDate ? ` · ~${row.expireDate.slice(2).replace(/-/g, '.')}` : ''}`;

  return (
        <EditBody>
          {/* ---------------- 왼쪽: 회원 찾기 ---------------- */}
          <PoolPanel sx={{ width: '520px' }}>
            <SearchRow>
              <SearchField>
                <span style={{ fontSize: '14px', color: '#a6a6a6' }}>🔍</span>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="이름 또는 전화번호를 입력해 주세요"
                />
              </SearchField>
              {/* 그룹 불러오기 — 왼쪽 목록을 그 그룹 소속 후보로 좁힌다. 담기는 체크 흐름 그대로. */}
              <FilterToggleButton active={!!groupId} onClick={e => setGroupAnchor(e.currentTarget)}>
                <GroupsOutlinedIcon sx={{ fontSize: 15 }} />
                <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedGroup ? selectedGroup.name : '그룹 불러오기'}
                </span>
              </FilterToggleButton>
            </SearchRow>

            <Menu
              open={!!groupAnchor}
              anchorEl={groupAnchor}
              onClose={() => setGroupAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    marginTop: '4px', minWidth: '220px', maxHeight: '320px', borderRadius: '10px',
                    border: '1px solid #E4E8EB', boxShadow: '0 10px 28px rgba(0,0,0,0.14)',
                  },
                },
              }}>
              <MenuItem selected={!groupId} onClick={() => { setGroupId(null); setGroupAnchor(null); }} sx={{ fontSize: '13px' }}>
                전체 회원
              </MenuItem>
              {groups.length === 0 ? (
                <MenuItem disabled sx={{ fontSize: '12.5px' }}>만들어 둔 그룹이 없습니다</MenuItem>
              ) : (
                groups.map(group => (
                  <MenuItem
                    key={group.id}
                    selected={groupId === group.id}
                    onClick={() => { setGroupId(group.id); setGroupAnchor(null); }}
                    sx={{ fontSize: '13px', display: 'flex', gap: '8px' }}>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {group.name}
                    </span>
                    <span style={{ flexShrink: 0, fontSize: '11.5px', color: '#8A939C' }}>{group.memberCount}명</span>
                  </MenuItem>
                ))
              )}
            </Menu>

            {/* 수강권 칩 — 켜면 그 수강권 보유 회원만(여러 개 = OR). 둘째 줄부터는 접어 두고 +N 으로 펼친다.
                수강권이 하나뿐이면(일괄예약 1단계에서 이미 고른 경우) 후보가 전부 그 보유자라 필터·토글이 의미 없어 숨긴다. */}
            {ticketOptions.length > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 16px', borderBottom: '1px solid #eceff1' }}>
                <Box
                  ref={setChipArea}
                  sx={{
                    position: 'relative', flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: `${CHIP_ROW_GAP}px`,
                    maxHeight: chipsExpanded
                      ? `${CHIP_ROW_HEIGHT * CHIP_EXPANDED_ROWS + CHIP_ROW_GAP * (CHIP_EXPANDED_ROWS - 1)}px`
                      : `${CHIP_ROW_HEIGHT}px`,
                    overflowY: chipsExpanded ? 'auto' : 'hidden',
                    scrollbarWidth: 'thin',
                  }}>
                  {visibleTicketOptions.map(t => {
                    const key = tkey(t.id, t.ticketType);
                    const active = activeTicketIds.includes(key);
                    const count = holderCountByTicket[key] || 0;
                    return (
                      <FilterChip key={key} active={active} onClick={() => toggleTicketChip(key)} style={{ gap: '5px' }}>
                        <span style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</span>
                        <span style={{
                          fontSize: '11.5px', fontWeight: 700,
                          color: active ? 'rgba(255,255,255,0.85)' : count > 0 ? '#0F7F85' : '#bfbfbf',
                        }}>
                          {count}
                        </span>
                      </FilterChip>
                    );
                  })}
                  {visibleTicketOptions.length === 0 && (
                    <Typography sx={{ fontSize: '12px', color: '#a6a6a6', lineHeight: `${CHIP_ROW_HEIGHT}px` }}>
                      보유 회원이 있는 수강권이 없습니다
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, height: `${CHIP_ROW_HEIGHT}px` }}>
                  {(hiddenChipCount > 0 || chipsExpanded) && (
                    <Typography
                      onClick={() => setChipsExpanded(v => !v)}
                      sx={{ fontSize: '12px', fontWeight: 700, color: '#4AB3BC', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {chipsExpanded ? '접기' : `+${hiddenChipCount} 더보기`}
                    </Typography>
                  )}
                  <Box onClick={() => setOnlyHeldTickets(v => !v)} sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                    <Checkbox checked={onlyHeldTickets} size="small" sx={{ p: 0, color: '#c4c4c4', '&.Mui-checked': { color: '#4AB3BC' } }} />
                    <Typography sx={{ fontSize: '12px', color: '#595959', whiteSpace: 'nowrap' }}>보유 회원만</Typography>
                  </Box>
                </Box>
              </Box>
            )}

            <ResultRow>
              <ResultLabel>{loading ? '조회 중...' : `조건 결과 ${poolRows.length}명`}</ResultLabel>
              <div style={{ flex: 1 }} />
              <AddAllButton
                ref={addButtonRef}
                tabIndex={checkedVisibleCount === 0 ? -1 : 0}
                disabled={checkedVisibleCount === 0}
                onClick={addChecked}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addChecked(); } }}
                sx={checkedVisibleCount > 0 ? {
                  outline: 'none',
                  boxShadow: '0 0 0 3px rgba(74,179,188,0.35)',
                  '&:focus-visible, &:focus': { boxShadow: '0 0 0 4px rgba(74,179,188,0.5)' },
                  animation: 'reservePickerPulse 1.2s ease-in-out 2',
                  '@keyframes reservePickerPulse': {
                    '0%, 100%': { boxShadow: '0 0 0 3px rgba(74,179,188,0.35)' },
                    '50%': { boxShadow: '0 0 0 7px rgba(74,179,188,0.12)' },
                  },
                } : {}}>
                {`선택 ${checkedVisibleCount}명 담기`}
              </AddAllButton>
            </ResultRow>

            <ListHead>
              <RowCheck checked={allVisibleChecked} onClick={toggleAllVisible} sx={{ cursor: 'pointer' }}>
                {allVisibleChecked ? '✓' : ''}
              </RowCheck>
              <HeadCell w="64px">이름</HeadCell>
              <HeadCell w="100px">전화번호</HeadCell>
              <HeadCell grow>수강권</HeadCell>
              <HeadCell w="136px">잔여 · 만료</HeadCell>
            </ListHead>

            <ListScroll>
              {loading ? (
                <EmptyMessage><CircularProgress size={20} sx={{ color: '#4ab3bc' }} /></EmptyMessage>
              ) : rows.length === 0 ? (
                <EmptyMessage>{groupId ? '이 그룹에는 선택한 수강권을 보유한 회원이 없습니다' : '선택한 수강권을 보유한 회원이 없습니다'}</EmptyMessage>
              ) : poolRows.length === 0 ? (
                <EmptyMessage>{search || activeTicketIds.length > 0 ? '조건에 맞는 회원이 없습니다' : '조건 결과가 모두 담겼습니다'}</EmptyMessage>
              ) : (
                poolRows.map((row, index) => {
                  const isChecked = checkedIds.has(row.memberId);
                  const alreadyAll = isAlreadyReservedAll(row, classCount);
                  const reservedOut = isFullyReserved(row);
                  const shortage = shortageOf(row);
                  const filterShort = shortage === 0 && usableUnderFilter(row).length === 0;
                  const blocked = alreadyAll || reservedOut || shortage > 0 || filterShort;
                  return (
                    <PoolRow
                      key={row.memberId} odd={index % 2 === 1} onClick={() => toggleChecked(row)}
                      style={{ userSelect: 'none', ...(blocked ? { cursor: 'not-allowed', opacity: 0.5, filter: 'grayscale(1)' } : {}) }}>
                      <RowCheck checked={isChecked} sx={blocked ? { borderColor: '#e0e0e0', backgroundColor: '#f5f5f5' } : {}}>{isChecked ? '✓' : ''}</RowCheck>
                      <NameCell w="64px" center>{row.name}</NameCell>
                      <Cell w="100px" center>{row.phoneNumber || '-'}</Cell>
                      <Cell grow title={rowTicketKeys(row).map(k => ticketNameById[k]).filter(Boolean).join(', ')}>
                        {renderTicketLabel(row)}
                      </Cell>
                      {blocked ? (
                        <Cell w="136px" center>
                          <Box component="span" sx={{ fontSize: '11px', fontWeight: 700, color: '#8A939C', backgroundColor: '#EEF1F3', borderRadius: '4px', px: '6px', py: '1px' }}>
                            {alreadyAll ? `예약된 회원입니다${row.alreadyReservedCount > 1 ? ` · ${row.alreadyReservedCount}회` : ''}`
                              : reservedOut ? '다른 수업 예약 중 · 잔여 0회'
                                : filterShort ? '고른 수강권 잔여 부족' : `잔여 세션 ${shortage}회 부족`}
                          </Box>
                        </Cell>
                      ) : (
                        <Cell w="136px" center sx={{ color: '#4AB3BC' }}>
                          {row.alreadyReservedCount > 0 ? (
                            /* 예약이 섞인 회원은 그 사실이 먼저 보여야 한다 — 잔여·만료는 마우스를 올리면 바뀐다. */
                            <Box component="span" title={renderRemaining(row)}
                              sx={{
                                fontSize: '11px', fontWeight: 700, color: '#8A939C', backgroundColor: '#EEF1F3',
                                borderRadius: '4px', px: '5px', py: '1px', whiteSpace: 'nowrap',
                                '& .on-hover': { display: 'none' },
                                '&:hover': {
                                  color: '#4AB3BC', backgroundColor: 'transparent', fontWeight: 400,
                                  '& .on-rest': { display: 'none' },
                                  '& .on-hover': { display: 'inline' },
                                },
                              }}>
                              <span className="on-rest">예약된 수업 {row.alreadyReservedCount}회 포함</span>
                              <span className="on-hover">{renderRemaining(row)}</span>
                            </Box>
                          ) : renderRemaining(row)}
                        </Cell>
                      )}
                    </PoolRow>
                  );
                })
              )}
            </ListScroll>
          </PoolPanel>

          <PanelGap />

          {/* ---------------- 오른쪽: 담긴 회원 ---------------- */}
          <PickedPanel>
            <PickedHeader>
              <PickedTitle>
                담긴 회원 <span>{picked.length}</span>{limit != null ? `/${limit}명` : '명'}
              </PickedTitle>
              <PickedSearchField>
                <span style={{ fontSize: '12px', color: '#a6a6a6' }}>🔍</span>
                <input value={pickedSearch} onChange={e => setPickedSearch(e.target.value)} placeholder="담긴 회원에서 찾기" />
              </PickedSearchField>
              <AddAllButton
                disabled={picked.length === 0}
                onClick={() => onPickedChange([])}
                sx={{ backgroundColor: picked.length === 0 ? '#cfd5d9' : '#fff', color: picked.length === 0 ? '#fff' : '#C2403D', border: `1px solid ${picked.length === 0 ? '#cfd5d9' : '#f0c9c7'}`, '&:hover': { backgroundColor: picked.length === 0 ? '#cfd5d9' : '#fdf3f1' } }}>
                전체 비우기
              </AddAllButton>
            </PickedHeader>

            <ListHead sx={{ padding: '6px 20px' }}>
              <HeadCell w="80px">이름</HeadCell>
              <HeadCell w="108px">전화번호</HeadCell>
              <HeadCell grow>예약에 쓸 수강권</HeadCell>
              <HeadCell w="140px">잔여 · 만료</HeadCell>
              <HeadCell w="22px" />
            </ListHead>

            <ListScroll>
              {picked.length === 0 ? (
                <EmptyMessage>왼쪽에서 회원을 찾아 담아 주세요</EmptyMessage>
              ) : visiblePicked.length === 0 ? (
                <EmptyMessage>검색과 일치하는 회원이 없습니다</EmptyMessage>
              ) : (
                visiblePicked.map((row, index) => {
                  // 쓸 수 있는 상품이 둘 이상이면 어느 것으로 예약할지 고른다(잔여가 모자란 상품은 목록에 없다).
                  const usable = usableTickets(row);
                  const all = row.tickets || [];
                  const usableKeys = new Set(usable.map(t => `${t.ticketType}:${t.courseTicketId}`));
                  // 저장값은 {productId, ticketType} — 수강권·일일권은 ID 체계가 달라 종류까지 맞춰야 한다.
                  const pick = memberTicketIds[row.memberId];
                  const chosen = (pick && usable.find(t => String(t.courseTicketId) === String(pick.productId)
                    && t.ticketType === pick.ticketType)) || usable[0] || null;
                  const chosenKey = chosen ? `${chosen.ticketType}:${chosen.courseTicketId}` : '';
                  return (
                    <PickedRow key={row.memberId} odd={index % 2 === 1}>
                      <NameCell w="80px" center>{row.name}</NameCell>
                      <Cell w="108px" center>{row.phoneNumber || '-'}</Cell>
                      <Cell grow>
                        {all.length > 1 && onMemberTicketChange ? (
                          <Select
                            size="small" value={chosenKey}
                            onChange={e => {
                              const [type, id] = String(e.target.value).split(':');
                              onMemberTicketChange(row.memberId, Number(id), type);
                            }}
                            sx={{ width: '100%', height: '28px', fontSize: '12.5px', backgroundColor: '#fff', '& .MuiSelect-select': { py: 0 } }}>
                            {/* 잔여가 모자란 상품도 목록에 남긴다 — 왜 못 고르는지 보이지 않으면 "다른 상품이 사라졌다"로 읽힌다. */}
                            {all.map(t => {
                              const key = `${t.ticketType}:${t.courseTicketId}`;
                              const short = !usableKeys.has(key);
                              return (
                                <MenuItem key={key} value={key} disabled={short} sx={{ fontSize: '12.5px' }}>
                                  {t.ticketType === 'TRIAL' ? `[일일권] ${t.ticketName}` : t.ticketName}
                                  <span style={{ color: short ? '#C2403D' : '#8c8c8c', marginLeft: 6 }}>
                                    {t.remainingCount == null ? '무제한' : `잔여 ${t.remainingCount}회`}
                                    {short ? ` · 이번 예약에 ${row.requiredCount ?? 0}회 필요` : ''}
                                  </span>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        ) : (chosen
                          ? (chosen.ticketType === 'TRIAL' ? `[일일권] ${chosen.ticketName}` : chosen.ticketName)
                          : renderTicketLabel(row))}
                      </Cell>
                      <Cell w="140px" center sx={{ color: '#4AB3BC' }}>
                        {chosen
                          ? `${chosen.remainingCount == null ? '무제한' : `잔여 ${chosen.remainingCount}회`}${chosen.expireDate ? ` · ~${chosen.expireDate.slice(2).replace(/-/g, '.')}` : ''}`
                          : renderRemaining(row)}
                      </Cell>
                      <RemoveRowButton onClick={() => removePicked(row.memberId)}>✕</RemoveRowButton>
                    </PickedRow>
                  );
                })
              )}
            </ListScroll>
          </PickedPanel>
        </EditBody>
  );
};

/** 그룹수업 생성 모달용 — 피커를 모달로 감싸고 "선택 완료"에서만 부모에 반영한다. */
const ReserveMemberSelectModal = ({ open, onClose, selectedMembers = [], onChangeMembers, maxCount, ...pickerProps }) => {
  const [picked, setPicked] = useState([]);
  const limit = toLimit(maxCount);

  useEffect(() => {
    if (open) setPicked(selectedMembers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal open={open} onClose={onClose}>
      <EditModalContainer sx={{ height: '640px' }}>
        <EditHeader>
          <EditHeaderTitle>함께 예약할 회원 선택</EditHeaderTitle>
          <EditHeaderClose onClick={onClose}>✕</EditHeaderClose>
        </EditHeader>

        <ReserveMemberPicker open={open} maxCount={maxCount} picked={picked} onPickedChange={setPicked} {...pickerProps} />

        <EditFooter>
          <SummaryText>
            <SummaryStrong>{picked.length}명</SummaryStrong>
            {limit != null && <SummaryMuted>/ 정원 {limit}명</SummaryMuted>}
            <SummaryMuted>· 생성되는 모든 회차에 예약됩니다</SummaryMuted>
          </SummaryText>
          <FooterActions>
            <CancelButton onClick={onClose}>취소</CancelButton>
            <ApplyButton onClick={() => { onChangeMembers(picked); onClose(); }}>선택 완료 ({picked.length}명)</ApplyButton>
          </FooterActions>
        </EditFooter>
      </EditModalContainer>
    </Modal>
  );
};

export default ReserveMemberSelectModal;
